import { Box } from '@chakra-ui/react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { Condition, Iteration } from 'Component/others';
import DataNotFound from 'Component/data-notfound';
import { useUserInfo } from 'Store/userInfo';
import { useToast } from 'Store/toast';
import useGetHostStockList from 'Domain/host-stock-promo/useGetHostStockList';
import CardStock from 'View/host-stock-promo/component/card-stock';
import CardStockSkeleton from 'View/host-stock-promo/component/skeleton/card-stock-skeleton';
import ListLoader from 'View/host-stock-promo/component/list-loader';
import { useHostStockPromoState } from 'View/host-stock-promo/state';
import handleRefetch from 'View/host-stock-promo/helper/handleRefetch';

import styles from './styles.module.css';

const Stock = () => {
  const { t } = useTranslation('host-stock-promo');
  const { filterState, updateFilterState, updateState, state } = useHostStockPromoState();
  const { isLoadingUser } = useUserInfo();
  const queryClient = useQueryClient();
  const [isSyncing, setIsSyncing] = useState(false);
  const { onOpenToast } = useToast();

  const { data: stockList, isFetching: isLoadingStock } = useGetHostStockList({ enabled: !isLoadingUser, params: filterState });

  const hasMoreData = stockList?.currentPage < stockList?.totalPage;
  const hasData = stockList?.stocks?.length > 0;
  const isInitialLoading = (isLoadingStock || isLoadingUser) && filterState.page === 1;

  const onLoadMore = () => {
    updateFilterState({ page: filterState.page + 1 });
  };

  // Listen for SSE Magpie Sync
  useEffect(() => {
    const showToastUpdate = () => {
      onOpenToast({
        status: 'success',
        type: 'mobile',
        description: t('success.auto_updated'),
        duration: 5,
        position: 'top-mobile',
      });
    };

    const onRefetch = () => {
      handleRefetch({ state, filterState, setIsSyncing, updateState, updateFilterState, queryClient, showToastUpdate });
    };

    window.addEventListener('host-stock-refetch', onRefetch);

    return () => {
      window.removeEventListener('host-stock-refetch', onRefetch);
    };
  }, [filterState, updateFilterState, updateState, queryClient, onOpenToast, state, t]);

  return (
    <InfiniteScroll dataLength={stockList?.stocks?.length ?? 0} hasMore={hasMoreData} next={onLoadMore} scrollThreshold="90%">
      <Box
        className={`${styles['stock-host__container']} ${isSyncing && styles['stock-host__container--hidden']}`}
        marginBottom={hasMoreData ? '50px' : '0px'}
        data-testid="stock"
      >
        <Condition statement={!isInitialLoading} fallback={<CardStockSkeleton />}>
          <Condition
            statement={hasData}
            fallback={<DataNotFound background="var(--color-semantic-white)" height="calc(100vh - 320px)" fieldSearch={filterState.search} />}
          >
            <Iteration key={stockList?.id} items={stockList?.stocks} render={(item) => <CardStock data={item} />} />
          </Condition>
        </Condition>
      </Box>

      <Condition statement={isSyncing}>
        <Box className={styles['stock-host-loader__container']}>
          <ListLoader />
        </Box>
      </Condition>
    </InfiniteScroll>
  );
};

export default Stock;
